import type { DeliveryGroupDraft, DeliveryGroupStopDraft, LegacyStopDraft } from './deliveryGroupTypes';
import { flattenAdditionalStopsFromGroups } from './routeStopsSavePipeline';

export type OrderedStopEntry = {
  stop: DeliveryGroupStopDraft;
  groupUid: string;
};

function collectStopContexts(groups: DeliveryGroupDraft[]): OrderedStopEntry[] {
  const seen = new Set<string>();
  const items: OrderedStopEntry[] = [];
  const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const group of sorted) {
    for (const stop of [...group.loadingStops, ...group.unloadingStops]) {
      if (seen.has(stop.uid)) continue;
      seen.add(stop.uid);
      items.push({ stop, groupUid: group.uid });
    }
  }
  return items;
}

function compareByDraftPosition(a: OrderedStopEntry, b: OrderedStopEntry): number {
  const pa = a.stop.position;
  const pb = b.stop.position;
  const aMissing = pa == null || !Number.isFinite(Number(pa)) || Number(pa) < 0;
  const bMissing = pb == null || !Number.isFinite(Number(pb)) || Number(pb) < 0;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (pa !== pb) return Number(pa) - Number(pb);
  return String(a.stop.uid).localeCompare(String(b.stop.uid));
}

/**
 * Global itinerary order — source of truth is draft.position, not loading/unloading buckets.
 */
export function getOrderedStops(groups: DeliveryGroupDraft[]): OrderedStopEntry[] {
  return [...collectStopContexts(groups)].sort(compareByDraftPosition);
}

/** Apply explicit global UID order and reindex positions 0..n-1. */
export function applyGlobalItineraryOrder(
  groups: DeliveryGroupDraft[],
  orderedUids: string[]
): DeliveryGroupDraft[] {
  const established = establishMissingStopPositions(groups);
  const posByUid = new Map(orderedUids.map((uid, index) => [uid, index]));
  return applyPositionMapToGroups(established, posByUid);
}

/** Move one stop up/down in global itinerary (position only; UIDs unchanged). */
export function moveStopInGlobalItinerary(
  groups: DeliveryGroupDraft[],
  stopUid: string,
  direction: -1 | 1
): DeliveryGroupDraft[] {
  const ordered = getOrderedStops(groups);
  const idx = ordered.findIndex((e) => e.stop.uid === stopUid);
  if (idx < 0) return groups;
  const nextIdx = idx + direction;
  if (nextIdx < 0 || nextIdx >= ordered.length) return groups;
  const swapped = [...ordered];
  [swapped[idx], swapped[nextIdx]] = [swapped[nextIdx], swapped[idx]];
  return applyGlobalItineraryOrder(groups, swapped.map((e) => e.stop.uid));
}

/** Legacy per-group bucket order (all loadings, then all unloadings). */
export function getLegacyBucketOrderedUids(groups: DeliveryGroupDraft[]): string[] {
  const uids: string[] = [];
  const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const g of sorted) {
    for (const s of g.loadingStops) uids.push(s.uid);
    for (const s of g.unloadingStops) uids.push(s.uid);
  }
  return uids;
}

export function getPositionOrderedUids(groups: DeliveryGroupDraft[]): string[] {
  return getOrderedStops(groups).map((e) => e.stop.uid);
}

export function allStopsHaveEditorPosition(groups: DeliveryGroupDraft[]): boolean {
  const contexts = collectStopContexts(groups);
  if (contexts.length === 0) return true;
  return contexts.every(
    (c) => c.stop.position != null && Number.isFinite(Number(c.stop.position)) && Number(c.stop.position) >= 0
  );
}

export function maxStopPosition(groups: DeliveryGroupDraft[]): number {
  let max = -1;
  for (const { stop } of collectStopContexts(groups)) {
    if (stop.position != null && Number(stop.position) > max) max = Number(stop.position);
  }
  return max;
}

export function nextStopPosition(groups: DeliveryGroupDraft[]): number {
  return maxStopPosition(groups) + 1;
}

function applyPositionMapToGroups(
  groups: DeliveryGroupDraft[],
  posByUid: Map<string, number>
): DeliveryGroupDraft[] {
  return groups.map((group) => {
    const patchList = (list: DeliveryGroupStopDraft[]) =>
      list.map((s) => {
        const next = posByUid.get(s.uid);
        if (next == null || next === s.position) return s;
        return { ...s, position: next };
      });
    const loadingStops = patchList(group.loadingStops);
    const unloadingStops = patchList(group.unloadingStops);
    const primaryLoadingPos =
      loadingStops.find((s) => s.position != null)?.position ??
      (group.isDefault ? 0 : group.loadingPosition);
    const changed =
      loadingStops.some((s, i) => s !== group.loadingStops[i]) ||
      unloadingStops.some((s, i) => s !== group.unloadingStops[i]) ||
      primaryLoadingPos !== group.loadingPosition;
    if (!changed) return group;
    return {
      ...group,
      loadingStops,
      unloadingStops,
      loadingPosition: group.isDefault ? 0 : Number(primaryLoadingPos)
    };
  });
}

/**
 * Fill missing draft.position from legacy bucket walk (loading block → unloading block per group).
 */
export function establishMissingStopPositions(groups: DeliveryGroupDraft[]): DeliveryGroupDraft[] {
  const contexts = collectStopContexts(groups);
  if (contexts.every((c) => c.stop.position != null && Number(c.stop.position) >= 0)) {
    return groups;
  }

  const posByUid = new Map<string, number>();
  let cursor = 0;
  const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const g of sorted) {
    for (const s of g.loadingStops) {
      if (s.position != null && s.position >= 0) {
        posByUid.set(s.uid, s.position);
        cursor = Math.max(cursor, s.position + 1);
      } else {
        posByUid.set(s.uid, cursor++);
      }
    }
    for (const s of g.unloadingStops) {
      if (s.position != null && s.position >= 0) {
        posByUid.set(s.uid, s.position);
        cursor = Math.max(cursor, s.position + 1);
      } else {
        posByUid.set(s.uid, cursor++);
      }
    }
  }
  return applyPositionMapToGroups(groups, posByUid);
}

/**
 * Preserve relative order from draft.position; optionally reindex to contiguous 0..n-1.
 */
export function syncGroupStopPositionsFromEditorOrder(
  groups: DeliveryGroupDraft[],
  options?: { reindexContiguous?: boolean }
): DeliveryGroupDraft[] {
  const reindex = options?.reindexContiguous !== false;
  const established = establishMissingStopPositions(groups);
  const ordered = getOrderedStops(established);
  const posByUid = new Map<string, number>();
  if (reindex) {
    ordered.forEach((entry, idx) => posByUid.set(entry.stop.uid, idx));
  } else {
    for (const entry of ordered) {
      if (entry.stop.position != null && entry.stop.position >= 0) {
        posByUid.set(entry.stop.uid, entry.stop.position);
      }
    }
  }
  return applyPositionMapToGroups(established, posByUid);
}

export function refreshGroupLoadingPositionsFromEditorOrder(
  groups: DeliveryGroupDraft[]
): DeliveryGroupDraft[] {
  const loadingByGroup = new Map<string, number>();
  for (const entry of getOrderedStops(groups)) {
    if (entry.stop.type !== 'loading') continue;
    if (!loadingByGroup.has(entry.groupUid)) {
      loadingByGroup.set(entry.groupUid, entry.stop.position ?? 0);
    }
  }
  return groups.map((g) => {
    const next = g.isDefault ? 0 : loadingByGroup.get(g.uid) ?? g.loadingPosition;
    if (next === g.loadingPosition) return g;
    return { ...g, loadingPosition: next };
  });
}

function groupStopToLegacyDraft(
  stop: DeliveryGroupStopDraft,
  group: DeliveryGroupDraft
): LegacyStopDraft {
  return {
    uid: stop.uid,
    id: stop.id ?? null,
    type: stop.type,
    position: stop.position ?? -1,
    pointType: stop.pointType ?? null,
    pointId: stop.pointId ?? null,
    parentLoadingPosition:
      stop.type === 'unloading'
        ? stop.parentLoadingPosition ?? (group.isDefault ? 0 : null)
        : null,
    parentLoadingStopId: stop.parentLoadingStopId ?? null,
    groupId: stop.groupId ?? group.groupId ?? null,
    groupUid: group.uid,
    address: stop.address ?? null,
    city: stop.city ?? null,
    contactPerson: stop.contactPerson ?? null,
    phone: stop.phone ?? null,
    plannedAt: stop.plannedAt ?? null,
    actualAt: stop.actualAt ?? null,
    notes: stop.notes ?? null,
    status: stop.status ?? null
  };
}

/** Additional stops only — positions follow editor order (not loading-first flatten). */
export function flattenDeliveryGroupsToLegacyStopsEditorOrder(
  groups: DeliveryGroupDraft[]
): LegacyStopDraft[] {
  const additional = flattenAdditionalStopsFromGroups(groups);
  const additionalUids = new Set(additional.map((s) => s.uid));
  const groupByUid = new Map<string, DeliveryGroupDraft>();
  for (const g of groups) groupByUid.set(g.uid, g);

  return getOrderedStops(groups)
    .filter((e) => additionalUids.has(e.stop.uid))
    .map((entry, idx) => {
      const group = groupByUid.get(entry.groupUid)!;
      const legacy = groupStopToLegacyDraft(entry.stop, group);
      legacy.position =
        entry.stop.position != null && entry.stop.position >= 0 ? entry.stop.position : idx + 1;
      return legacy;
    });
}
